import type {
  FieldFields,
  FieldInstanceFields,
  FieldInstanceFieldsWithBaseValues,
  PropertyDataType,
} from "@regrello/graphql-api";
import { isDevServer } from "../../../../../../utils/environmentUtils";
import { getErrorMessageWithPayload } from "../../../../../../utils/getErrorMessageWithPayload";
import type { CustomFieldPlugin } from "../types/CustomFieldPlugin";

// The `CustomFieldPlugin` interface is designed to be generic, allowing each plugin to specify its
// own value type (boolean, string, number, etc.). The registrar needs to store all these different
// plugin types in a single array. There are some members in the `CustomFieldPlugin` interface that
// return specific types. `unknown` is insufficient for this and results in type errors when
// defining plugins within this array. Thus, we use `any` instead.
//
// biome-ignore lint/suspicious/noExplicitAny: For reasoning, see above.
export const customFieldPlugins: Array<CustomFieldPlugin<any>> = [];

export const CustomFieldPluginRegistrar = {
  /**
   * A hardcoded list of known custom-field plugins. When introducing a new custom field, all you have
   * to do is add it to this array, and it'll work throughout the frontend.
   */
  getAllPlugins(): Array<CustomFieldPlugin<unknown>> {
    return customFieldPlugins;
  },

  /**
   * Returns the one custom-field plugin that is compatible with the provided field.
   *
   * @throws if no compatible custom-field plugin is found, or if multiple are found
   * @deprecated since Oct 9, 2025. Instead, please use
   * `SpectrumFieldPluginRegistrar.getPluginForSpectrumField` to ensure we're using Field Versions
   * when available. All loaded field instances now include an accompanying Field Version that you
   * can use with this lookup function.
   */
  getPluginForField(field: FieldFields): CustomFieldPlugin<unknown> {
    const matches: Array<CustomFieldPlugin<unknown>> = [];

    for (const customFieldPlugin of customFieldPlugins) {
      if (customFieldPlugin.canProcessField(field)) {
        matches.push(customFieldPlugin);
      }
    }

    if (matches.length === 0) {
      throw new Error(
        getErrorMessageWithPayload(
          "Could not find a custom-field plugin that was able to process the current field. " +
            "This is a developer error. " +
            "Please review the `canProcessField` logic in all custom-field plugins.",
          { field },
        ),
      );
    }

    if (matches.length > 1) {
      const uniqueUris = new Set(matches.map(({ uri }) => uri));
      if (uniqueUris.size === 1 && isDevServer()) {
        // In Dev mode, the same plugin may be registered multiple times due to Fast Refresh.
        // Just return the freshest one without throwing an error.
        return matches[matches.length - 1];
      }

      throw new Error(
        getErrorMessageWithPayload(
          "Multiple custom-field plugins claimed to be able to process the current field. " +
            "Only one custom-field plugin should be able to process any given field. " +
            "This is a developer error. " +
            "Please review the `canProcessField` logic in the custom-field plugins mentioned here.",
          {
            field,
            ostensiblyCompatibleCustomFieldPluginUris: matches.map(({ uri }) => uri),
          },
        ),
      );
    }

    return matches[0];
  },

  /**
   * Returns the one custom-field plugin that is compatible with the provided field instance.
   *
   * @throws if no compatible custom-field plugin is found, or if multiple are found
   * @deprecated since Oct 9, 2025. Instead, please use
   * `SpectrumFieldPluginRegistrar.getPluginForSpectrumField` to ensure we're using Field Versions
   * when available. All loaded field instances now include an accompanying Field Version that you
   * can use with this lookup function.
   */
  getPluginForFieldInstance(
    fieldInstance: FieldInstanceFields | FieldInstanceFieldsWithBaseValues,
  ): CustomFieldPlugin<unknown> {
    const matches: Array<CustomFieldPlugin<unknown>> = [];

    for (const customFieldPlugin of customFieldPlugins) {
      if (customFieldPlugin.canProcessFieldInstance(fieldInstance)) {
        matches.push(customFieldPlugin);
      }
    }

    if (matches.length === 0) {
      throw new Error(
        getErrorMessageWithPayload(
          "Could not find a custom-field plugin that was able to process the current field instance. " +
            "This is a developer error. " +
            "Please review the `canProcessFieldInstance` logic in all custom-field plugins.",
          { fieldInstance },
        ),
      );
    }

    if (matches.length > 1) {
      const uniqueUris = new Set(matches.map(({ uri }) => uri));
      if (uniqueUris.size === 1 && isDevServer()) {
        // In Dev mode, the same plugin may be registered multiple times due to Fast Refresh.
        // Just return the freshest one without throwing an error.
        return matches[matches.length - 1];
      }

      throw new Error(
        getErrorMessageWithPayload(
          "Multiple custom-field plugins claimed to be able to process the current field instance. " +
            "Only one custom-field plugin should be able to process any given field instance. " +
            "This is a developer error. " +
            "Please review the `canProcessFieldInstance` logic in the custom-field plugins mentioned here.",
          {
            fieldInstance,
            ostensiblyCompatibleCustomFieldPluginUris: matches.map(({ uri }) => uri),
          },
        ),
      );
    }

    return matches[0];
  },

  /**
   * Returns an array of custom-field plugins that are compatible with the provided
   * `PropertyDataType`. This is useful for determining which select field options (or form fields
   * in general) should be shown to users without having a `FieldInstanceFields` handy for providing
   * to the `getPluginForFieldInstance` function.
   *
   * It may also return an empty array.
   */
  getPluginsForPropertyDataType(propertyDataType: PropertyDataType): Array<CustomFieldPlugin<unknown>> {
    const matches: Array<CustomFieldPlugin<unknown>> = [];

    for (const customFieldPlugin of customFieldPlugins) {
      if (customFieldPlugin.canProcessPropertyDataType(propertyDataType)) {
        matches.push(customFieldPlugin);
      }
    }

    return matches;
  },
};
